import { useMemo, useState } from 'react';

/**
 * Utilidad centralizada de ordenamiento client-side para las 3 tablas de la
 * vista Global (`TablaSucursales`, "Productos más negados" y "Detalle de
 * negados por talla" en `NegadosCards`) — feedback del usuario: "en cada
 * campo, ayúdame a poder ordenar (sort) en las 3 tablas". Las filas que
 * consumen estas tablas YA vienen acotadas al top 40 por negados/consultas
 * desde el backend (`buildSucursalTableRows`, `GET
 * /dashboard/negados-productos`, `GET /dashboard/negados-tallas`): ordenar
 * aquí solo reordena esas 40 filas, nunca trae más datos ni cambia el corte
 * — no es un bug, es sort puramente client-side sobre lo ya cargado.
 */
export type SortDir = 'asc' | 'desc';

/**
 * `true` si el valor debe tratarse como "vacío" para efectos de orden:
 * `null`, `undefined` o `NaN` (un número inválido). Estos SIEMPRE se van al
 * final de la lista sin importar `dir` — no tiene sentido premiar "el peor
 * dato" solo por invertir la dirección de un clic.
 */
function isEmptyValue(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));
}

/**
 * Devuelve el valor numérico si `v` es un número o un string PURAMENTE numérico
 * (ej. la talla `"26"` o `"7.5"`, que llegan como string); `null` si no lo es
 * (nombres/descripciones/estatus). Sirve para que columnas numéricas-como-string
 * (talla) ordenen natural — `7` antes que `26` — en vez de caracter a caracter
 * (donde `"26"` iría antes que `"7"`).
 */
function asNumeric(v: unknown): number | null {
  if (typeof v === 'number') {
    return Number.isNaN(v) ? null : v;
  }
  if (typeof v === 'string' && /^\s*\d+(\.\d+)?\s*$/.test(v)) {
    return Number(v);
  }
  return null;
}

/**
 * Ordena `rows` por `key` sin mutar el arreglo original (devuelve una copia).
 * Estable: en empates conserva el orden relativo previo (se desempata por el
 * índice original, así no dependemos de que el `Array.sort` del motor sea
 * estable). `null`/`undefined`/`NaN` siempre al final, en ambas direcciones.
 * Números se comparan por valor; el resto (strings) con
 * `localeCompare('es')` para que acentos/eñes ordenen "natural" (nombres de
 * sucursal/producto).
 */
export function sortRows<T>(rows: T[], key: keyof T, dir: SortDir): T[] {
  const indexed = rows.map((row, index) => ({ row, index }));

  indexed.sort((a, b) => {
    const va = a.row[key];
    const vb = b.row[key];
    const aEmpty = isEmptyValue(va);
    const bEmpty = isEmptyValue(vb);

    if (aEmpty && bEmpty) {
      return a.index - b.index;
    }
    if (aEmpty) {
      return 1; // a vacío -> siempre al final
    }
    if (bEmpty) {
      return -1; // b vacío -> siempre al final
    }

    let cmp: number;
    const na = asNumeric(va);
    const nb = asNumeric(vb);
    if (na !== null && nb !== null) {
      // Ambos numéricos (números o strings numéricos como la talla): por valor.
      cmp = na - nb;
    } else {
      cmp = String(va).localeCompare(String(vb), 'es');
    }

    if (cmp === 0) {
      return a.index - b.index; // empate real -> estabilidad por orden previo
    }
    return dir === 'asc' ? cmp : -cmp;
  });

  return indexed.map((w) => w.row);
}

export interface TableSortState<T> {
  key: keyof T;
  dir: SortDir;
}

/**
 * Hook chico que administra `{key, dir}` para una tabla ordenable: expone
 * `sorted` (las filas ya ordenadas, memoizadas), `sort` (el estado activo,
 * para pintar el indicador ▲/▼ en el encabezado correspondiente) y
 * `toggle(key)` — clic en una columna nueva ordena desc; un 2º clic en la
 * MISMA columna invierte a asc (y así sucesivamente).
 */
export function useTableSort<T>(rows: T[], defaultKey: keyof T, defaultDir: SortDir = 'desc') {
  const [sort, setSort] = useState<TableSortState<T>>({ key: defaultKey, dir: defaultDir });

  const sorted = useMemo(() => sortRows(rows, sort.key, sort.dir), [rows, sort.key, sort.dir]);

  function toggle(key: keyof T) {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
      }
      return { key, dir: 'desc' };
    });
  }

  return { sorted, sort, toggle };
}
